import { useState } from "react";
import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { Button, ConfigProvider, Flex, Form, Select, Typography } from "antd";
import { translate, translateWithVars } from "shared/languages";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { PAYROLL_WEEKDAY_OPTIONS, PayrollFrequency } from "shared/types/enum";
import { payrollSettingsOptions } from "hooks/queries/payroll";
import {
  EFFECTIVE_MONTH_REQUIRED,
  useCancelPendingPayrollSettings,
  useUpdatePayrollSettings,
} from "hooks/mutations/payroll";
import { showConfirmModal } from "components/modals/confirmModal";
import EffectiveMonthPicker, { EffectiveMonth } from "pages/payroll/EffectiveMonthPicker";
import WarningBox from "components/WarningBox";
import { localDateOnly, localTzDate, tzDate } from "shared/utils/dayjs";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

export const getFrequencyLabels = (): Record<PayrollFrequency, string> => ({
  [PayrollFrequency.Weekly]: translate("payrollPage.settings.frequency_weekly"),
  [PayrollFrequency.TwiceMonthly]: translate("payrollPage.settings.frequency_twice_monthly"),
  [PayrollFrequency.Monthly]: translate("payrollPage.settings.frequency_monthly"),
});

// The example period under the frequency field, shown for Twice Monthly
// only: "Contoh: 1 Okt - 15 Okt 2026" is the one place that says the month
// splits on the 15th (mirrors computePeriodForClosingDate on the server,
// services/payroll/period.ts). Monthly and Weekly got an example too until
// 2026-10-13, but theirs repeated what the option and the closing-day select
// already say, so they were dropped. Examples only, no explanation sentence
// (Andrew, 2026-10-09).
export const frequencyHint = (frequency?: PayrollFrequency): string | null => {
  if (frequency !== PayrollFrequency.TwiceMonthly) return null;
  const today = tzDate().startOf("day");
  return translateWithVars("payrollPage.settings.hint_example", {
    example: `${localTzDate(today.startOf("month")).format("D MMM")} - ${localTzDate(today.date(15)).format("D MMM YYYY")}`,
  });
};

const hintStyle = { fontSize: textPx(12.5), color: colors.ink2, lineHeight: 1.45 };

// The payroll frequency control, shared by the setup guide's first step
// (`inline`: fields and button on one row) and the Pengaturan Penggajian
// card (`stacked`). The values start empty for a studio that never saved,
// because the server answers a synthetic Monthly default with exists=false
// and showing it as chosen made the step look already done (the 2026-09-16
// demo saved nothing here, then every downstream save failed).
const FrequencyForm = ({
  layout,
  onSaved,
  onCancel,
}: {
  layout: "inline" | "stacked";
  onSaved?: () => void;
  onCancel?: () => void;
}) => {
  const { data: settings } = useQuery(payrollSettingsOptions());
  const { mutate: updateSettings, isPending } = useUpdatePayrollSettings();
  const { mutate: cancelPending, isPending: isCancelling } = useCancelPendingPayrollSettings();
  const [frequency, setFrequency] = useState<PayrollFrequency | undefined>(undefined);
  const [weeklyClosingDay, setWeeklyClosingDay] = useState<number | null | undefined>(undefined);

  const currentFrequency = frequency ?? (settings?.exists ? settings.frequency : undefined);
  const currentWeeklyDay = weeklyClosingDay !== undefined ? weeklyClosingDay : settings?.exists ? settings.weekly_closing_day : null;
  const isWeekly = currentFrequency === PayrollFrequency.Weekly;
  const hint = frequencyHint(currentFrequency);

  const pending = settings?.exists ? settings.pending_change : null;
  const previous = settings?.exists ? settings.previous_change : null;

  // The select falls back to the server's value only while untouched, so a
  // picked value would otherwise outlive the save and keep showing a cadence
  // the server did not put in force.
  const resetLocal = () => {
    setFrequency(undefined);
    setWeeklyClosingDay(undefined);
  };

  const save = (effective?: EffectiveMonth) => {
    if (!currentFrequency) return;
    const form = {
      frequency: currentFrequency,
      weekly_closing_day: isWeekly ? currentWeeklyDay ?? null : null,
      ...(effective ? { effective } : {}),
    };
    updateSettings(form, {
      onSuccess: () => {
        resetLocal();
        onSaved?.();
      },
      onError: (error) => {
        // The studio has runs, so only the owner knows whether "switch to
        // Monthly" means this month or the next. The server also says whether
        // this month is still open (closed once a run touches it); an older
        // server sends no flag, which reads as open.
        const response = (error as AxiosError<{ message?: string; data?: unknown }>).response;
        const message = String(response?.data?.message ?? "");
        if (!message.includes(EFFECTIVE_MONTH_REQUIRED) || effective) return;
        const months = response?.data?.data as { this_month?: string; next_month?: string; this_month_allowed?: boolean } | undefined;
        if (!months?.this_month || !months?.next_month) return;
        let choice: EffectiveMonth = "next_month";
        showConfirmModal({
          title: translate("payrollPage.settings.effective_title"),
          content: (
            <EffectiveMonthPicker
              thisMonth={months.this_month}
              nextMonth={months.next_month}
              thisMonthAllowed={months.this_month_allowed !== false}
              onChange={(value) => {
                choice = value;
              }}
            />
          ),
          confirmLabel: translate("payrollPage.settings.save_button"),
          onConfirm: () => save(choice),
        });
      },
    });
  };

  const handleSave = () => save();

  // Nothing to save when the form already matches what is in force, or what
  // is queued: reverting a queued change is "Batalkan perubahan", not Simpan.
  const matches = (f?: PayrollFrequency, day?: number | null) =>
    !!currentFrequency &&
    f === currentFrequency &&
    (currentFrequency !== PayrollFrequency.Weekly || (day ?? null) === (currentWeeklyDay ?? null));
  const unchanged =
    (settings?.exists && matches(settings.frequency, settings.weekly_closing_day)) ||
    (!!pending && matches(pending.frequency, pending.weekly_closing_day));

  // One line about the change, with the undo beside it while it can still be
  // undone: queued (not started yet) or in force but not yet used by a run.
  // Once a run has been generated under it, the line becomes the plain note
  // about which cadence the earlier periods still follow.
  const startedSince = settings?.exists ? settings.current_change_since : null;
  const cancellable = settings?.exists ? settings.change_cancellable : false;
  // A change the studio can still undo is a state worth seeing before the
  // save button, not a grey line after it, so it uses the same Alert shape
  // the Run Payroll page uses for a conflict: one sentence, the action beside
  // it. Capped in width because the card is far wider than the sentence.
  // Every date below is a bare Jakarta calendar day from the server, so it is
  // formatted with localDateOnly; localTzDate would parse it in the browser's
  // zone and show the day before on a machine east of Jakarta.
  // Gaji Pokok and recurring items are one amount per person with no
  // frequency attached, so a frequency change never touches them. Said with
  // the change, and with WHEN to act: a change that starts later must not
  // have the amounts changed early, or the last periods of the old cadence
  // would pay the new cadence's figure.
  // Two bold facts, the change itself and that the salary amounts stay as
  // they are, then one regular line saying when to act. The salary fact used
  // to sit mid-body after the date and was easy to skim past.
  const changeMessage: { fact: string[]; action: string } | null = pending
    ? {
        fact: [
          translateWithVars("payrollPage.settings.pending_note", {
            frequency: getFrequencyLabels()[pending.frequency],
            date: localDateOnly(pending.effective_from).format("D MMM YYYY"),
          }),
          translate("payrollPage.settings.salary_unchanged"),
        ],
        action: translateWithVars("payrollPage.settings.salary_adjust_after", {
          date: localDateOnly(pending.effective_from).format("D MMM YYYY"),
        }),
      }
    : startedSince && settings?.exists
      ? {
          fact: [
            translateWithVars("payrollPage.settings.started_note", {
              // The cadence the SERVER has in force, never the one picked in
              // the select: an unsaved pick would otherwise make this line
              // claim a frequency is in force that nothing has been paid under.
              frequency: getFrequencyLabels()[settings.frequency],
              date: localDateOnly(startedSince).format("D MMM YYYY"),
            }),
            translate("payrollPage.settings.salary_unchanged"),
          ],
          action: translate("payrollPage.settings.salary_check_now"),
        }
      : null;

  const changeNotes = (
    <>
      {cancellable && changeMessage && (
        // The same box the payroll flow uses for every note, in its info
        // tone: a change queued or just started is a state, not a fault. The
        // undo changes data, so it stays an ordinary button beside the text.
        <WarningBox
          tone="info"
          items={[changeMessage]}
          extra={
            <Button
              size="small"
              loading={isCancelling}
              // Reset the local pick too: the select falls back to the
              // server's value only while untouched, so an unsaved pick would
              // otherwise keep showing a cadence the undo has just reverted.
              onClick={() => cancelPending(undefined, { onSuccess: resetLocal })}
            >
              {translate("payrollPage.settings.pending_cancel")}
            </Button>
          }
        />
      )}
      {/* Only once there is nothing to act on: with the undo still available
          the line above already says what changed. */}
      {!cancellable && previous && (
        <Typography.Text type="secondary" style={hintStyle}>
          {translateWithVars("payrollPage.settings.previous_note", {
            frequency: getFrequencyLabels()[previous.frequency],
            date: localDateOnly(previous.until).format("D MMM YYYY"),
          })}
        </Typography.Text>
      )}
    </>
  );

  const frequencySelect = (
    <Select
      style={{ width: 240 }}
      aria-label={translate("payrollPage.settings.frequency_label")}
      placeholder={translate("payrollPage.settings.frequency_placeholder")}
      value={currentFrequency}
      onChange={setFrequency}
      options={Object.values(PayrollFrequency).map((f) => ({ value: f, label: getFrequencyLabels()[f] }))}
    />
  );
  const daySelect = (
    <Select
      style={{ width: layout === "inline" ? 200 : 240 }}
      aria-label={translate("payrollPage.settings.closing_day_label")}
      placeholder={translate("payrollPage.settings.closing_day_placeholder")}
      value={currentWeeklyDay ?? undefined}
      onChange={setWeeklyClosingDay}
      options={PAYROLL_WEEKDAY_OPTIONS.map((o) => ({ value: o.value, label: translate(`payrollPage.weekday.${o.value}`) }))}
    />
  );
  const saveButton = (
    <ConfigProvider theme={modalButtonsTheme}>
      <Button
        type="primary"
        loading={isPending}
        disabled={!currentFrequency || (isWeekly && currentWeeklyDay === null) || unchanged}
        onClick={handleSave}
      >
        {translate("payrollPage.settings.save_button")}
      </Button>
    </ConfigProvider>
  );

  if (layout === "inline") {
    // The step title names the first field, so only the closing day carries
    // a visible label; bottoms align so the button sits on the field line.
    return (
      <Flex vertical gap={8} align="flex-start">
        <Form layout="vertical" component="div">
          <Flex gap={10} wrap align="flex-end">
            <Form.Item style={{ marginBottom: 0 }}>{frequencySelect}</Form.Item>
            {isWeekly && (
              <Form.Item label={translate("payrollPage.settings.closing_day_label")} style={{ marginBottom: 0 }}>
                {daySelect}
              </Form.Item>
            )}
            {saveButton}
            {onCancel && <Button onClick={onCancel}>{translate("button.cancel")}</Button>}
          </Flex>
        </Form>
        {hint && <Typography.Text style={hintStyle}>{hint}</Typography.Text>}
      </Flex>
    );
  }

  return (
    <Form layout="vertical">
      <Form.Item
        label={translate("payrollPage.settings.frequency_label")}
        // A schedule change is allowed at any time (a run's period is
        // stored on the run, so nothing already created moves). What it
        // does change is the NEXT period, which the server clips so it
        // never pays a day a previous run already paid (period.ts). Said
        // in the label's tooltip, Andrew's call, not as a paragraph.
        tooltip={translate("payrollPage.settings.change_note")}
        extra={!isWeekly && hint ? <span style={hintStyle}>{hint}</span> : undefined}
        style={{ marginBottom: 14 }}
      >
        {frequencySelect}
      </Form.Item>
      {isWeekly && (
        <Form.Item
          label={translate("payrollPage.settings.closing_day_label")}
          style={{ marginBottom: 14 }}
        >
          {daySelect}
        </Form.Item>
      )}
      <Flex vertical gap={14}>
        {changeNotes}
        <div>{saveButton}</div>
      </Flex>
    </Form>
  );
};

export default FrequencyForm;
